import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Button,
  Tabs,
  Tab,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  TextField,
  Link,
  useTheme,
  alpha,
  Breadcrumbs,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  SwapHoriz as ConvertIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingIcon,
  Comment as CommentIcon,
  Send as SendIcon,
  NavigateNext as NavigateNextIcon,
  ArrowBack as ArrowBackIcon,
  Web as WebIcon,
  Category as IndustryIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

// Import custom components
import { Card, MagicLoader, TextField as DesignTextField } from '../../design-system';
import { TabPanel } from '../../components/ui';
import { EntityStatusBadge } from '../../components/core';
import SecureAvatar from '../../components/ui/SecureAvatar';
import useConfirmDialog from '../../hooks/useConfirmationDialog';

// Import hooks and stores
import useLeadStore from '../../stores/leadStore';
import useAuthStore from '../../stores/authStore';
import useNotificationStore from '../../stores/notificationStore';

// Import utilities
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/formatters';
import { useThemeTokens, useGradientText } from '../../theme/themeHooks';

const statusColors = {
  new: 'info',
  contacted: 'primary',
  qualified: 'secondary',
  proposal: 'warning',
  negotiation: 'warning',
  won: 'success',
  lost: 'error'
};

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { tokens } = useThemeTokens();
  const { style: titleGradient } = useGradientText();
  
  // Local state
  const [activeTab, setActiveTab] = useState(0);
  const [comment, setComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // Store hooks
  const selectedLead = useLeadStore(state => state.selectedLead);
  const loading = useLeadStore(state => state.loading);
  const fetchLeadById = useLeadStore(state => state.fetchLeadById);
  const clearSelectedLead = useLeadStore(state => state.clearSelectedLead);
  const deleteLead = useLeadStore(state => state.deleteLead);
  const addComment = useLeadStore(state => state.addComment);
  
  const { hasPermission } = useAuthStore();
  const { showSuccess, showError } = useNotificationStore();
  
  // Confirmation dialog
  const [ConfirmDialog, confirm] = useConfirmDialog();
  
  // Permission checks
  const canUpdate = hasPermission('leads', 'update');
  const canDelete = hasPermission('leads', 'delete');
  
  useEffect(() => {
    fetchLeadById(id);
    return () => {
      clearSelectedLead();
    };
  }, [id, fetchLeadById, clearSelectedLead]);

  const handleEdit = () => {
    navigate(`/leads/${id}/edit`);
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: `Delete ${selectedLead.name}?`,
      content: `This will permanently delete the lead. This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDelete: true
    });
    
    if (confirmed) {
      try {
        await deleteLead(id);
        showSuccess(`Lead "${selectedLead.name}" deleted successfully`);
        navigate('/leads');
      } catch (error) {
        showError(`Failed to delete lead: ${error.message}`);
      }
    }
  };

  const handleConvert = () => {
    navigate(`/leads/${id}/convert`);
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    
    setSubmittingComment(true);
    try {
      await addComment(id, {
        text: comment,
        isInternal: false
      });
      setComment('');
      showSuccess('Comment added successfully');
    } catch (error) {
      showError('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Loading state
  if (loading || !selectedLead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <MagicLoader variant="mystical" size="large" message="Loading lead details..." />
      </Box>
    );
  }

  const lead = selectedLead;

  // Render lead details section
  const renderLeadDetails = () => (
    <Grid container spacing={3}>
      {/* Contact Information */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon />
              Contact Information
            </Typography>
            <List>
              {lead.email && (
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Email"
                    secondary={
                      <Link href={`mailto:${lead.email}`} underline="hover">
                        {lead.email}
                      </Link>
                    }
                  />
                </ListItem>
              )}
              {lead.phone && (
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Phone"
                    secondary={
                      <Link href={`tel:${lead.phone}`} underline="hover">
                        {lead.phone}
                      </Link>
                    }
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemIcon>
                  <AssignmentIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Source"
                  secondary={lead.source?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                />
              </ListItem>
              {lead.assignedTo && (
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Assigned To"
                    secondary={`${lead.assignedTo.firstName} ${lead.assignedTo.lastName}`}
                  />
                </ListItem>
              )}
            </List>
          </CardContent>
        </Card>
      </Grid>

      {/* Company Information */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon />
              Company Information
            </Typography>
            <List>
              {lead.company && (
                <ListItem>
                  <ListItemIcon>
                    <BusinessIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Company"
                    secondary={lead.company}
                  />
                </ListItem>
              )}
              {lead.title && (
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Job Title"
                    secondary={lead.title}
                  />
                </ListItem>
              )}
              {lead.website && (
                <ListItem>
                  <ListItemIcon>
                    <WebIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Website"
                    secondary={
                      <Link href={lead.website} target="_blank" rel="noopener" underline="hover">
                        {lead.website}
                      </Link>
                    }
                  />
                </ListItem>
              )}
              {lead.industry && (
                <ListItem>
                  <ListItemIcon>
                    <IndustryIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Industry"
                    secondary={lead.industry}
                  />
                </ListItem>
              )}
            </List>
          </CardContent>
        </Card>
      </Grid>

      {/* Opportunity Information */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MoneyIcon />
              Opportunity Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Estimated Value</Typography>
                  <Typography variant="h6">
                    {lead.estimatedValue ? formatCurrency(lead.estimatedValue) : 'Not specified'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Probability</Typography>
                  <Typography variant="h6">{lead.probability}%</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Expected Close Date</Typography>
                  <Typography variant="h6">
                    {lead.expectedCloseDate ? formatDate(lead.expectedCloseDate) : 'Not set'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Last Contact</Typography>
                  <Typography variant="h6">
                    {lead.lastContactedAt ? formatDate(lead.lastContactedAt) : 'Never'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            {lead.description && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>Description</Typography>
                <Typography variant="body1">{lead.description}</Typography>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Render comments section
  const renderComments = () => (
    <Box>
      {/* Add comment form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Add Comment</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <DesignTextField
              multiline
              rows={3}
              placeholder="Add a comment..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleAddComment}
              disabled={!comment.trim() || submittingComment}
              magical={true}
            >
              {submittingComment ? 'Adding...' : 'Add'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Comments list */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Comments ({lead.comments?.length || 0})</Typography>
          {lead.comments && lead.comments.length > 0 ? (
            <List>
              {lead.comments.map((comment, index) => (
                <ListItem key={index} alignItems="flex-start">
                  <ListItemAvatar>
                    <Avatar>
                      {comment.user?.firstName?.charAt(0) || comment.user?.name?.charAt(0) || 'U'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2">
                          {comment.user?.firstName ? `${comment.user.firstName} ${comment.user.lastName}` : comment.user?.name || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(comment.createdAt)}
                        </Typography>
                      </Box>
                    }
                    secondary={comment.text}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No comments yet. Be the first to add one!
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Box sx={{ py: 3 }}>
      {/* Breadcrumbs navigation */}
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link 
          underline="hover" 
          color="inherit" 
          component={RouterLink} 
          to="/leads"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <PersonIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Leads
        </Link>
        <Typography color="text.primary">{lead.name}</Typography>
      </Breadcrumbs>
      
      {/* Header with lead info and actions */}
      <Card 
        magical={true} 
        level={2}
        sx={{ mb: 4, overflow: 'visible' }}
      >
        <CardContent>
          <Grid container spacing={3} alignItems="flex-start">
            {/* Avatar and basic info */}
            <Grid item xs={12} lg={8}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SecureAvatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    mr: 3,
                    borderRadius: tokens.borderRadius.card,
                  }}
                >
                  {lead.name.charAt(0)}
                </SecureAvatar>
              
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Typography 
                      variant="h4" 
                      component="h1" 
                      sx={{ fontWeight: 600, ...titleGradient }}
                    >
                      {lead.name}
                    </Typography>
                    <Chip
                      label={lead.status}
                      color={statusColors[lead.status] || 'default'}
                      size="medium"
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    {lead.company && (
                      <EntityStatusBadge 
                        status={lead.company}
                        icon={<BusinessIcon fontSize="small" />}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    <EntityStatusBadge 
                      status={lead.source?.replace('_', ' ')}
                      size="small"
                      variant="outlined"
                    />
                    {lead.estimatedValue && (
                      <Typography variant="body2" color="text.secondary">
                        Value: {formatCurrency(lead.estimatedValue)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
            
            {/* Action buttons */}
            <Grid item xs={12} lg={4}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'row', lg: 'row' },
                justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                gap: 2,
                flexWrap: 'wrap'
              }}>
                {canUpdate && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={handleEdit}
                      magical={true}
                    >
                      Edit
                    </Button>
                    {lead.status !== 'won' && lead.status !== 'lost' && (
                      <Button
                        variant="contained"
                        startIcon={<ConvertIcon />}
                        onClick={handleConvert}
                        magical={true}
                      >
                        Convert to Client
                      </Button>
                    )}
                  </>
                )}
                {canDelete && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Details" />
          <Tab label="Comments" />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          <TabPanel value={activeTab} index={0}>
            {renderLeadDetails()}
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            {renderComments()}
          </TabPanel>
        </Box>
      </Card>

      <ConfirmDialog />
    </Box>
  );
};

export default LeadDetail;